import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { StatusBadge, type MonitorStatus } from '../ui/StatusBadge';
import { MonitorTypeBadge, type MonitorType } from '../ui/MonitorTypeBadge';

/**
 * Monitor data for the header
 */
export interface MonitorHeaderData {
  id: string;
  name: string;
  url: string;
  type: MonitorType;
  status: MonitorStatus;
  interval: number;
  lastCheckedAt: Date | string | null;
}

/**
 * Quick stats data
 */
export interface MonitorQuickStats {
  uptimePercentage: number | null;
  avgResponseTime: number | null;
  checksToday: number;
}

/**
 * MonitorHeader component props
 */
interface MonitorHeaderProps {
  monitor: MonitorHeaderData;
  stats?: MonitorQuickStats;
  onPause?: () => void;
  onResume?: () => void;
  onDelete?: () => void;
  isLoading?: boolean;
  isPausing?: boolean;
  isDeleting?: boolean;
}

/**
 * Format interval for display
 */
function formatInterval(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${seconds / 60}m`;
  return `${seconds / 3600}h`;
}

/**
 * Format relative time
 */
function formatRelativeTime(date: Date | string | null): string {
  if (!date) return 'Never';

  const now = new Date();
  const checkDate = new Date(date);
  const diffMs = now.getTime() - checkDate.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

/**
 * Format response time
 */
function formatResponseTime(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * Loading skeleton for the header
 */
function MonitorHeaderSkeleton() {
  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
        }}
      >
        {/* Left: Monitor Info */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Skeleton variant="rounded" width={80} height={32} />
            <Skeleton variant="text" width={200} height={40} />
          </Box>
          <Skeleton variant="text" width={300} height={24} />
          <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
            <Skeleton variant="text" width={100} height={20} />
            <Skeleton variant="text" width={100} height={20} />
            <Skeleton variant="text" width={100} height={20} />
          </Box>
        </Box>

        {/* Right: Actions */}
        <Box sx={{ display: 'flex', gap: 1, alignSelf: 'flex-start' }}>
          <Skeleton variant="rounded" width={100} height={36} />
          <Skeleton variant="rounded" width={100} height={36} />
        </Box>
      </Box>
    </Paper>
  );
}

/**
 * MonitorHeader component
 * Displays monitor name, URL, status, and quick stats with action buttons
 */
export function MonitorHeader({
  monitor,
  stats,
  onPause,
  onResume,
  onDelete,
  isLoading = false,
  isPausing = false,
  isDeleting = false,
}: MonitorHeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const menuOpen = Boolean(anchorEl);
  const isPaused = monitor.status === 'PAUSED';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePauseResume = () => {
    handleMenuClose();
    if (isPaused) {
      onResume?.();
    } else {
      onPause?.();
    }
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    onDelete?.();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  if (isLoading) {
    return <MonitorHeaderSkeleton />;
  }

  return (
    <>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 3 },
          }}
        >
          {/* Left: Monitor Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Name and Status */}
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 2 },
                mb: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <StatusBadge status={monitor.status} size="medium" />
                <MonitorTypeBadge type={monitor.type} size="medium" />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {monitor.name}
              </Typography>
            </Box>

            {/* URL */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography
                component="a"
                href={monitor.url}
                target="_blank"
                rel="noopener noreferrer"
                variant="body1"
                color="text.secondary"
                sx={{
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.main',
                  },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: { xs: '100%', sm: 400 },
                }}
              >
                {monitor.url}
              </Typography>
              <Tooltip title="Open in new tab">
                <IconButton
                  component="a"
                  href={monitor.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Quick Stats */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 2, sm: 3 },
              }}
            >
              {/* Uptime */}
              <Tooltip title="Uptime (last 30 days)">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TrendingUpIcon
                    sx={{
                      fontSize: 18,
                      color:
                        stats?.uptimePercentage == null
                          ? 'text.secondary'
                          : stats.uptimePercentage >= 99
                            ? '#10B981'
                            : stats.uptimePercentage >= 95
                              ? '#F59E0B'
                              : '#EF4444',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color:
                        stats?.uptimePercentage == null
                          ? 'text.secondary'
                          : stats.uptimePercentage >= 99
                            ? '#10B981'
                            : stats.uptimePercentage >= 95
                              ? '#F59E0B'
                              : '#EF4444',
                    }}
                  >
                    {stats?.uptimePercentage != null
                      ? `${stats.uptimePercentage.toFixed(2)}%`
                      : '-'}
                  </Typography>
                </Box>
              </Tooltip>

              {/* Average Response Time */}
              <Tooltip title="Average response time">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SpeedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {formatResponseTime(stats?.avgResponseTime ?? null)}
                  </Typography>
                </Box>
              </Tooltip>

              {/* Checks Today */}
              <Tooltip title="Checks today">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {stats?.checksToday ?? 0} checks
                  </Typography>
                </Box>
              </Tooltip>

              {/* Last Check */}
              <Tooltip title="Last check">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {formatRelativeTime(monitor.lastCheckedAt)}
                  </Typography>
                </Box>
              </Tooltip>

              {/* Check Interval */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  px: 1,
                  py: 0.25,
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                }}
              >
                Every {formatInterval(monitor.interval)}
              </Typography>
            </Box>
          </Box>

          {/* Right: Actions */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignSelf: { xs: 'stretch', md: 'flex-start' },
              flexDirection: { xs: 'row', sm: 'row' },
              justifyContent: { xs: 'flex-end', sm: 'flex-end' },
            }}
          >
            {!isMobile && (
              <>
                <Button
                  component={RouterLink}
                  to={`/monitors/${monitor.id}/edit`}
                  variant="outlined"
                  startIcon={<EditIcon />}
                  size="small"
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  startIcon={isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                  onClick={handlePauseResume}
                  disabled={isPausing}
                  size="small"
                >
                  {isPaused ? 'Resume' : 'Pause'}
                </Button>
              </>
            )}

            {/* More Menu */}
            <IconButton onClick={handleMenuOpen} size="small">
              <MoreVertIcon />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {isMobile && (
                <>
                  <MenuItem
                    component={RouterLink}
                    to={`/monitors/${monitor.id}/edit`}
                    onClick={handleMenuClose}
                  >
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                  </MenuItem>

                  <MenuItem onClick={handlePauseResume} disabled={isPausing}>
                    <ListItemIcon>
                      {isPaused ? (
                        <PlayArrowIcon fontSize="small" />
                      ) : (
                        <PauseIcon fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText>{isPaused ? 'Resume' : 'Pause'}</ListItemText>
                  </MenuItem>

                  <Divider />
                </>
              )}

              <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                <ListItemIcon>
                  <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Delete Monitor</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Monitor</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{monitor.name}</strong>? This action cannot be
            undone. All check history and statistics will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MonitorHeader;
